import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
} from 'react-native';

/**
 * Render-windowing for long lists.
 *
 * The mental model for this app: data is local (SQLite). Filtered lists
 * live entirely in JS memory. The *only* reason to "paginate" is render
 * cost — a phone choking on 800 task rows in a ScrollView. So we don't
 * paginate data at all; we just slice the *render* and grow the slice
 * as the user scrolls toward the bottom.
 *
 * Trigger strategy (the tricky part on web):
 *
 *   - **Native (iOS/Android)**: the RN ScrollView is the actual scroll
 *     surface. onScroll fires reliably with contentOffset/contentSize/
 *     layoutMeasurement. We use those to detect distance-from-bottom
 *     and call loadMore when within `triggerDistance`.
 *
 *   - **Web**: the *window* is what scrolls, not the inner RN
 *     ScrollView (RN-Web sizes ScrollViews to their content height
 *     when the flex chain doesn't constrain them). onScroll on the
 *     inner ScrollView never fires, contentOffset stays 0, and the
 *     hook can't detect bottom-approach. So the InfiniteFooter
 *     component owns the trigger on web using IntersectionObserver:
 *     when the footer scrolls into the visible viewport, it calls
 *     loadMore directly. The hook exposes loadMore for the footer.
 *
 * Why we deliberately don't try to make onScroll-trigger work on web:
 *
 *   We could listen on window.scroll instead — but that requires
 *   coordinating "is this the active ScrollView's window?" across
 *   multiple lists on the same page, and it doesn't compose if a
 *   page nests a scroller inside another (e.g. media modal inside a
 *   scroller). IntersectionObserver on the footer is the natural
 *   "did the user reach the boundary?" detector and works regardless
 *   of which element is actually scrolling.
 */

export interface InfiniteWindowOptions {
  /** How many extra items to add each time we extend. Default 30. */
  pageSize?: number;
  /** How many to show on first render / after a reset. Default = pageSize. */
  initialSize?: number;
  /** Pixels-from-bottom at which to auto-extend during scroll on native. */
  triggerDistance?: number;
}

export interface InfiniteWindow<T> {
  /** The slice to actually render. */
  visible: T[];
  /** Whether there are more items beyond the current window. */
  hasMore: boolean;
  /** Total count of items in the source array. */
  total: number;
  /** Advance the window by one page. Idempotent at the tail.
   *  InfiniteFooter calls this from its IntersectionObserver on web,
   *  and the onScroll handler calls it on native. Safe to call any
   *  number of times — at-tail calls become no-ops. */
  loadMore: () => void;
  /** Force the window back to `initialSize`. */
  reset: () => void;
  /** Native-only: bind to the parent ScrollView's `onScroll`.
   *  On web this is a no-op (RN-Web doesn't fire scroll events on
   *  ScrollViews that aren't actually scrolling). */
  onScroll: (e: NativeSyntheticEvent<NativeScrollEvent>) => void;
  /** Bind to the parent ScrollView's `onLayout` (native viewport size). */
  onLayout: (e: LayoutChangeEvent) => void;
}

export function useInfiniteWindow<T>(
  items: T[],
  options: InfiniteWindowOptions = {}
): InfiniteWindow<T> {
  const pageSize = options.pageSize ?? 30;
  const initialSize = options.initialSize ?? pageSize;
  const triggerDistance = options.triggerDistance ?? 600;
  const [count, setCount] = useState(initialSize);

  // Reset the window whenever the source array changes length (filter
  // change, fresh data load). We compare lengths instead of references
  // because useMemo'd filters produce new refs every render even when
  // the underlying data is identical.
  const prevLenRef = useRef(items.length);
  useEffect(() => {
    if (prevLenRef.current !== items.length) {
      setCount(initialSize);
      prevLenRef.current = items.length;
    }
  }, [items.length, initialSize]);

  const itemsLength = items.length;
  const loadMore = useCallback(() => {
    setCount((c) => {
      if (c >= itemsLength) return c;
      return Math.min(c + pageSize, itemsLength);
    });
  }, [itemsLength, pageSize]);

  const reset = useCallback(() => {
    setCount(initialSize);
  }, [initialSize]);

  // Native-only scroll measurements. On web these refs never get
  // populated (no scroll events) but reading them in the no-op handler
  // is harmless.
  const scrollYRef = useRef(0);
  const viewportHeightRef = useRef(0);
  const contentHeightRef = useRef(0);

  const onScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
      scrollYRef.current = contentOffset.y;
      viewportHeightRef.current = layoutMeasurement.height;
      contentHeightRef.current = contentSize.height;
      const distance =
        contentSize.height - (contentOffset.y + layoutMeasurement.height);
      if (distance < triggerDistance) loadMore();
    },
    [loadMore, triggerDistance]
  );

  const onLayout = useCallback((e: LayoutChangeEvent) => {
    viewportHeightRef.current = e.nativeEvent.layout.height;
  }, []);

  const visible = count >= items.length ? items : items.slice(0, count);
  return {
    visible,
    hasMore: count < items.length,
    total: items.length,
    loadMore,
    reset,
    onScroll,
    onLayout,
  };
}
